import React from "react";
import { Box, Chip, Stack, Tooltip, Typography } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { colorForSite } from "../../../utils/featureDistribution";
import NumericHistogram from "./NumericHistogram";
import CategoricalBars from "./CategoricalBars";

// Single card in the Feature Distribution panel grid.
//
// Shape (per /api/datasets/feature-distribution response.features[i]):
//   { feature, display_name, dtype: "numeric"|"categorical", plot,
//     bin_edges?|categories?, distributions: [...], site_warnings?: [...] }
//
// Renders:
//   - title row (with LABEL pill when feature === "__label__")
//   - dispatched chart (NumericHistogram or CategoricalBars)
//   - per-site footer chips: "{display_name}: M valid / K missing"
//   - site_warnings strip (when present) — e.g. OICR missing `age` column
const FeatureCard = ({ feature }) => {
  if (!feature || !feature.feature) return null;
  const isLabel = feature.feature === '__label__';
  const isNumeric = feature.dtype === 'numeric';
  const dists = Array.isArray(feature.distributions) ? feature.distributions : [];
  const warnings = Array.isArray(feature.site_warnings) ? feature.site_warnings : [];

  return (
    <Box
      sx={{
        bgcolor: 'var(--fx-paper)',
        border: '1px solid var(--fx-border)',
        borderRadius: 2,
        p: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        minWidth: 0,
        boxShadow: 'var(--fx-shadow-sm)',
        transition: 'border-color 140ms ease, box-shadow 140ms ease, transform 140ms ease',
        '&:hover': {
          borderColor: 'var(--fx-border-strong)',
          boxShadow: 'var(--fx-shadow-md)',
          transform: 'translateY(-1px)',
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
        {isLabel && (
          <Chip
            size="small"
            label="LABEL"
            sx={{
              height: 16,
              fontSize: '0.55rem',
              fontWeight: 800,
              letterSpacing: 0.5,
              bgcolor: '#1B365D',
              color: '#ffffff',
            }}
          />
        )}
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            fontSize: '0.85rem',
            color: 'var(--fx-text-strong)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={feature.display_name || feature.feature}
        >
          {feature.display_name || feature.feature}
        </Typography>
        {/* dev-0.7.40 — per-feature description tooltip. Backend returns
            `description` (1–3 sentence clinical phrasing) on every
            non-label feature; null for __label__ (no tooltip needed)
            and may be undefined when running against an older backend
            bundle. Truthy check skips both gracefully. */}
        {feature.description ? (
          <Tooltip
            title={feature.description}
            placement="top"
            arrow
            enterDelay={250}
            componentsProps={{
              tooltip: {
                sx: {
                  maxWidth: 320,
                  fontSize: '0.72rem',
                  lineHeight: 1.4,
                  bgcolor: '#0f172a',
                },
              },
              arrow: { sx: { color: '#0f172a' } },
            }}
          >
            <Box
              component="span"
              tabIndex={0}
              aria-label={`Description: ${feature.description}`}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                cursor: 'help',
                ml: 0.25,
                color: '#94a3b8',
                transition: 'color 120ms',
                '&:hover, &:focus-visible': { color: '#475569' },
                outline: 'none',
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 14 }} />
            </Box>
          </Tooltip>
        ) : null}
        <Box sx={{ flex: 1 }} />
        <Chip
          size="small"
          label={isNumeric ? 'numeric' : 'categorical'}
          sx={{
            height: 16,
            fontSize: '0.55rem',
            fontWeight: 700,
            letterSpacing: 0.4,
            bgcolor: isNumeric ? 'var(--fx-info-soft)' : 'var(--fx-warning-soft)',
            color: isNumeric ? 'var(--fx-info-text)' : 'var(--fx-warning-text)',
          }}
        />
      </Stack>

      {/* Chart */}
      {isNumeric ? (
        <NumericHistogram feature={feature} />
      ) : (
        <CategoricalBars feature={feature} />
      )}

      {/* Per-site valid/missing footer */}
      {dists.length > 0 && (
        <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.5, mt: 0.25 }}>
          {dists.map((d, idx) => {
            const isWarned = warnings.some((w) => w.capability_id === d.capability_id);
            return (
              <Tooltip
                key={d.site_id || idx}
                title={`${d.display_name || d.site_id} · valid=${d.n_valid ?? 'n/a'} · missing=${d.n_missing ?? 'n/a'}`}
              >
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.4,
                    px: 0.6,
                    py: 0.15,
                    borderRadius: 0.75,
                    bgcolor: isWarned ? 'var(--fx-warning-soft)' : 'var(--fx-subtle)',
                    border: `1px solid ${isWarned ? 'var(--fx-warning-border)' : 'var(--fx-border)'}`,
                  }}
                >
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      bgcolor: colorForSite(d.site_id, idx),
                    }}
                  />
                  <Typography sx={{ fontSize: '0.6rem', color: 'var(--fx-text-secondary)', fontWeight: 600 }}>
                    {d.display_name?.split(' ')[0] || d.site_id}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.6rem',
                      color: 'var(--fx-text-muted)',
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    }}
                  >
                    {d.n_valid ?? 0}
                  </Typography>
                  {(d.n_missing ?? 0) > 0 && (
                    <Typography
                      sx={{
                        fontSize: '0.6rem',
                        color: '#b45309',
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                      }}
                    >
                      ({d.n_missing} miss)
                    </Typography>
                  )}
                </Box>
              </Tooltip>
            );
          })}
        </Stack>
      )}

      {/* Per-feature warnings (e.g. COLUMN_MISSING for a single site) */}
      {warnings.length > 0 && (
        <Stack
          direction="row"
          alignItems="flex-start"
          spacing={0.5}
          sx={{
            mt: 0.25,
            p: 0.75,
            bgcolor: 'var(--fx-warning-soft)',
            border: '1px solid var(--fx-warning-border)',
            borderRadius: 0.75,
          }}
        >
          <WarningAmberIcon sx={{ fontSize: 14, color: '#b45309', mt: '1px' }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {warnings.map((w, i) => (
              <Typography
                key={i}
                sx={{ fontSize: '0.65rem', color: '#78350f', lineHeight: 1.4 }}
              >
                {w.code === 'COLUMN_MISSING'
                  ? `Column "${w.column || feature.feature}" missing at ${w.capability_id}`
                  : `${w.code}${w.column ? ` (${w.column})` : ''} @ ${w.capability_id}`}
              </Typography>
            ))}
          </Box>
        </Stack>
      )}
    </Box>
  );
};

export default FeatureCard;
